/**
 * Handshake mechanism for TwoPoint Widget
 */

import type { TwoPointWidgetConfig } from './types';
import { debugLog } from './utils';

/**
 * Send handshake message to iframe with retry mechanism
 * Implements exponential backoff: 100ms → 200ms → 400ms → 800ms → 1600ms → 2000ms
 */
export const sendHandshake = (
  iframe: HTMLIFrameElement,
  config: Required<TwoPointWidgetConfig>,
  retryCount: number = 0
): void => {
  const { maxRetries, retryDelay, debugMode, onHandshakeComplete, onHandshakeFailed } = config;
  const targetOrigin = new URL(iframe.src).origin;
  const widgetId = iframe.id;

  try {
    // Check if iframe is ready (may throw cross-origin error)
    const iframeOrigin = iframe.contentWindow?.location?.origin;

    if (iframeOrigin && iframeOrigin === targetOrigin) {
      debugLog(debugMode, `Iframe ready (attempt ${retryCount + 1}), sending handshake to`, widgetId);
      iframe.contentWindow!.postMessage(
        {
          type: 'TWIPOINT_PARENT_INIT',
          parentOrigin: window.location.origin + window.location.pathname,
          widgetId
        },
        targetOrigin
      );

      if (onHandshakeComplete) {
        onHandshakeComplete(widgetId, retryCount);
      }
      return;
    }
  } catch (e) {
    // Cross-origin access blocked - expected, try postMessage anyway
    try {
      debugLog(debugMode, `Attempt ${retryCount + 1} - sending handshake to`, widgetId);
      iframe.contentWindow!.postMessage(
        {
          type: 'TWIPOINT_PARENT_INIT',
          parentOrigin: window.location.origin + window.location.pathname,
          widgetId
        },
        targetOrigin
      );
    } catch (postError) {
      debugLog(debugMode, `Attempt ${retryCount + 1} failed:`, (postError as Error).message);
    }
  }

  // Retry with exponential backoff
  if (retryCount < maxRetries) {
    const delay = Math.min(retryDelay * Math.pow(2, retryCount), 2000);
    setTimeout(() => sendHandshake(iframe, config, retryCount + 1), delay);
  } else {
    debugLog(debugMode, `Max retries reached for`, widgetId);
    if (onHandshakeFailed) {
      onHandshakeFailed(widgetId);
    }
  }
};
